'use client';

import Head from 'next/head';
import ApiLayout from '@/app/components/ApiLayout';
import CardSection from '@/app/components/CardSection';
import HighlightBox from '@/app/components/HighlightBox';
import { generateApiSchemaObject } from '@/lib/schemaUtils';

const dataLayersApiData = {
  apiName: 'Weather Data Layers',
  apiDescription: 'Comprehensive reference for Tomorrow.io Weather API data layers, including field descriptors, units, timestep availability, and field availability criteria.',
  documentationUrl: 'https://docs.tomorrow.io/reference/data-layers',
  potentialActions: [
    {
      type: 'HttpRequestAction',
      name: 'Get Weather Data Fields',
      description: 'Retrieve weather data fields with specified units and location',
      target: 'https://api.tomorrow.io/v4/weather/forecast',
      method: 'GET' as const,
      expects: [
        {
          name: 'location',
          valueType: 'Text',
          valueRequired: true,
          description: 'Location coordinates or address'
        },
        {
          name: 'fields',
          valueType: 'Text',
          valueRequired: true,
          description: 'Array of weather data fields to retrieve'
        },
        {
          name: 'units',
          valueType: 'Text',
          valueRequired: false,
          description: 'Unit system: metric or imperial'
        }
      ]
    }
  ]
};

export default function DataLayersPage() {
  return (
    <ApiLayout
      title="Weather Data Layers"
      description="Comprehensive guide to Tomorrow.io Weather API data layers, field descriptors, and availability criteria"
      activeLink="data-layers-overview"
    >
      <Head>
        <title>Tomorrow.io LLM-Friendly Weather API Documentation | Data Layers</title>
        <meta name="description" content="Comprehensive reference for Tomorrow.io Weather API data layers, including field descriptors, units, timestep availability, and field availability criteria." />
      </Head>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateApiSchemaObject(
            dataLayersApiData.apiName,
            dataLayersApiData.apiDescription,
            dataLayersApiData.documentationUrl,
            dataLayersApiData.potentialActions
          ))
        }}
      />

      <CardSection title="Layer Subscriptions">
        <p>
          With more than 80 different data fields available, including weather, air quality, pollen, and fire index, you can build almost anything weather-related using the Tomorrow.io REST Weather API. You can also use the data points to create custom Insights that will trigger events whenever the conditions are met.
        </p>
        
        <HighlightBox>
          <strong>Complete Field Reference:</strong> For detailed field descriptors with both metric and imperial units, including all enumerated values and weather codes, see our comprehensive <a href="/data-layers/field-descriptors.md" className="text-blue-600 hover:text-blue-800 underline">Field Descriptors Reference</a>.
        </HighlightBox>

        <p>
          <a href="#" className="text-blue-600 hover:text-blue-800 underline">Here</a> are best practices for calling the API for different timesteps and layers.
        </p>
      </CardSection>

      <CardSection title="Timestep Availability">
        <p>
          Timesteps are restricted as following, in the intersection with the field-specific availability:
        </p>

        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-2 text-left">Timestep</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Interval Span</th>
                <th className="border border-gray-300 px-4 py-2 text-left">Availability</th>
                <th className="border border-gray-300 px-4 py-2 text-left">License</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 px-4 py-2">1d</td>
                <td className="border border-gray-300 px-4 py-2">1 day</td>
                <td className="border border-gray-300 px-4 py-2">[-7d,+15d]</td>
                <td className="border border-gray-300 px-4 py-2">Free</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-4 py-2">1h</td>
                <td className="border border-gray-300 px-4 py-2">1 hour</td>
                <td className="border border-gray-300 px-4 py-2">[-168h,+360h]</td>
                <td className="border border-gray-300 px-4 py-2">Free</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">30m</td>
                <td className="border border-gray-300 px-4 py-2">30 minutes</td>
                <td className="border border-gray-300 px-4 py-2">[-6h,+6h]</td>
                <td className="border border-gray-300 px-4 py-2">Enterprise</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-4 py-2">15m</td>
                <td className="border border-gray-300 px-4 py-2">15 minutes</td>
                <td className="border border-gray-300 px-4 py-2">[-6h,+6h]</td>
                <td className="border border-gray-300 px-4 py-2">Enterprise</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">5m</td>
                <td className="border border-gray-300 px-4 py-2">5 minutes</td>
                <td className="border border-gray-300 px-4 py-2">[-6h,+6h]</td>
                <td className="border border-gray-300 px-4 py-2">Enterprise</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-4 py-2">1m</td>
                <td className="border border-gray-300 px-4 py-2">1 minute</td>
                <td className="border border-gray-300 px-4 py-2">[-6h,+6h]</td>
                <td className="border border-gray-300 px-4 py-2">Enterprise</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">current</td>
                <td className="border border-gray-300 px-4 py-2">realtime</td>
                <td className="border border-gray-300 px-4 py-2">[0,0]</td>
                <td className="border border-gray-300 px-4 py-2">Free</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <p>
          Data availability beyond -6H in the past and beyond 4.5 days in the future is available for paid plans as an add-on only.
        </p>
      </CardSection>

      <CardSection title="Field Availability">
        <p>
          When detailing the different fields available for each Data Layer, we also specify its availability using the criteria annotation mentioned below:
        </p>

        <p>
          <strong>Frame:</strong> the time frame in which numerical data is supported -
        </p>
        <ul className="list-disc pl-6 mt-2">
          <li><strong>[ and ] Square Brackets:</strong> means the time frame includes the edge intervals</li>
          <li><strong>( and ) Parenthesis:</strong> means the time frame excludes the edge intervals</li>
          <li>In any case of two different timesteps - like "m" (minutes), "h" (hours) or "d" (days) - the data is available for any timesteps options between them</li>
        </ul>

        <p>
          <strong>Interfaces:</strong> where the data is supported -
        </p>
        <ul className="list-disc pl-6 mt-2">
          <li><strong>Timeline ( T ):</strong> queried data point for the conditions for any interval in a given time frame, in one or multiple timesteps</li>
          <li><strong>Insights ( I ):</strong> PARAMETER representation in a conditions rule structure, to be used in Alerts or Dashboards - currently supports only Point locations</li>
          <li><strong>Map ( M ):</strong> raster representation of a single field for a given time</li>
          <li><strong>Route ( R ):</strong> queried data point for the conditions throughout all intervals of the legs of the route, in a single timestep</li>
        </ul>

        <p>
          <strong>Area:</strong> the regional coverage in which the data is supported -
        </p>
        <ul className="list-disc pl-6 mt-2">
          <li><strong>WW:</strong> worldwide</li>
          <li><strong>LAND:</strong> land-only</li>
          <li><strong>MARINE:</strong> marine-only</li>
          <li><strong><a href="#" className="text-blue-600 hover:text-blue-800 underline">ISO 3166-1</a> codes:</strong> such as US or GB</li>
        </ul>

        <p>
          <strong>Suffix (S):</strong> the postfix for which data is supported -
        </p>
        <ul className="list-disc pl-6 mt-2">
          <li><strong>Max (x):</strong> maximum - supported for any location type, non-"1d" timestep for point locations will resolve the raw value. Example: <code>temperatureMax</code></li>
          <li><strong>Min (n):</strong> minimum - supported for any location type, non-"1d" timestep for point locations will resolve the raw value. Example: <code>temperatureMin</code></li>
          <li><strong>Avg (-):</strong> average - supported for any location type, non-"1d" timestep for point locations will resolve the raw value. Example: <code>temperatureAvg</code></li>
          <li><strong>MinTime/MaxTime (%):</strong> time where Min/Max values occurred - currently only supported in "1d" timestep. Examples: <code>temperatureMinTime</code>, <code>temperatureMaxTime</code></li>
        </ul>

        <p>
          <strong>Probabilistic Forecasting (P):</strong> Does this field supports Probabilistic Forecasting.
        </p>
        <ul className="list-disc pl-6 mt-2">
          <li><strong>P -:</strong> Means it doesn't support Probabilistic Forecasting.</li>
          <li><strong>P +:</strong> Means it does support Probabilistic Forecasting.</li>
        </ul>
      </CardSection>

      <CardSection>
        <HighlightBox>
          <strong>Availability Cases</strong>
        </HighlightBox>

        <p>
          In any cases where a field is not available in a requested timestep, the request will result in a <code>Timestep_Bounded_Field</code> warning. For example, <code>rainAccumulation</code> is supported only for <code>1h</code> and above; requesting <code>1m</code> will result in a warning.
        </p>

        <p>
          In any cases where a field is not available in a requested area, the request will result in a <code>Location_Bounded_Field</code> warning and will not be included in the response. For example, <code>hailBinary</code> is supported only for the US and over Europe will result in a warning.
        </p>

        <p>
          If a field is available, but the weather phenomena don't occur over the request location, the request will result in <code>null</code> values. For example, <code>moonriseTime</code> is supported only for days the moon rises above the horizon; requesting <code>moonriseTime</code> for a day with no moon rise will result in <code>null</code>.
        </p>
      </CardSection>
    </ApiLayout>
  );
} 